import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalBaseComponent } from '../add-or-edit-org-modal/modal-base.component';
import { Account } from '../data/account';
import { DataService } from '../data/data.service';
import { OrganizationAdministrator, OrganizationDetail } from '../data/organization-detail';
import { VisibleAccount } from '../data/visible-account';
import swal from 'sweetalert2/dist/sweetalert2.js';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { OnboardingStatus } from '../data/onboarding-status';
import { AuthService } from '../auth.service';

@Component({
  selector: 'app-assign-onboarding-modal',
  templateUrl: './assign-onboarding-modal.component.html',
  styleUrls: ['./assign-onboarding-modal.component.css']
})
export class AssignOnboardingModalComponent extends ModalBaseComponent<Account> implements OnInit {

  constructor(modalService: NgbModal, dataService: DataService) { super(modalService, dataService); }

  @Input() organization: OrganizationDetail;
  @Input() onboardingStep: "rostering"|"device readiness"|"training";
  @Input() currentAssignee: string;

  isWaiting: boolean = false;
  emailPreCheck: boolean = false;

  private searchAccountResults: VisibleAccount[];
  searching = false;

  emailExists() {
    return this.emailPreCheck || (this.searchAccountResults != null && this.searchAccountResults.find(a => a.Email === this.data.Email));
  }

  ngOnInit() {
  }

  compareRoles(item1, item2) {
    return item1 && item2 ? item1.Code === item2.Code : item1 === item2;
  }


  initializeNewData() {
    this.data = new Account();
    this.data.Email = '';
    this.data.Name = '';
    this.data.Password = '';
    this.data.ExternalID = '';
  }

  isInputValid(): boolean {
    var validForNewUser = 
      this.data.Name
      && this.data.Email
      && this.data.Email.search("@") !== -1;

    return validForNewUser || !!this.emailExists();
  }

  saveNew() {
    if (!this.emailExists()) {
      this.createAccount();
    } else {
      var verifiedAccount = this.searchAccountResults.find(a => a.Email === this.data.Email);
      var newAdmin = new OrganizationAdministrator();
      newAdmin.AccountID = verifiedAccount.AccountID;
      newAdmin.RoleCode = 'Admin';
      this.addExistingAdministrator(newAdmin);
    }
  }

  saveEdit(): void {
    this.saveNew();
  }

  createAccount(): void {
    this.isWaiting = true;
    this.dataService.createAccount({
      Name: this.data.Name,
      Email: this.data.Email,
      Password: null,
      ExternalID: this.data.ExternalID,
      TrialDays: null,
      OrganizationRole: 'GADMIN',
      OrganizationID: this.organization.ID,
      RoleCode: 'Admin',
      SendWelcomeEmail: false,
      WelcomeEmailDomain: this.dataService.getDomain(),
      ClassID: this.organization.ClassSection != null ? this.organization.ClassSection.ClassID : null
    }).subscribe(a => this.finishAssigning(a, true));
  }

  addExistingAdministrator(newAdministrator: OrganizationAdministrator) {
    if (!newAdministrator.AccountID) {
      swal.fire('Oops!', 'Account required.', 'error');
      return false;
    }
    if (this.organization.Administrators.some(admin => admin.AccountID === newAdministrator.AccountID)) {
      this.finishAssigning(newAdministrator, false);
    }
    else {
      const newOrg = { ...this.organization, Administrators: [...this.organization.Administrators] };
      newOrg.Administrators.push(newAdministrator);
      this.dataService.updateOrganization(newOrg).subscribe(result => {
        this.finishAssigning(result, false);
      });
    }
  }

  finishAssigning(result: any, newAccount: boolean): void {
    if (result === undefined) swal.fire('Oops!', 'Something went wrong', 'error');
    else {
      this.dataService.assignOnboardingStep(this.organization.ID, this.generateUpdateObject(newAccount)).subscribe(o => {
        super.saveComplete(o);
      });
    }
  }

  searchAccounts = (text$: Observable<string>) =>
    text$.pipe(
      tap(term => this.searching = term.length > 2),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => {
        if (term.length > 2) {
          return this.dataService.searchAccountsByEmail(term).pipe
            (
              tap(items => {
                this.searchAccountResults = items;
              }),
              map(items => items.filter(i => i.IsVisibileToRequestingAccount)),
              map(va => va.map(v => v.Email))
            );
        }
        else {
          return of([]);
        }
      }),
      tap(() => this.searching = false),
      tap(() => this.emailPreCheck = false)
    )


  accountLookupSelected(selectedAccountEmail: string) {
    this.data.Email = selectedAccountEmail;
  }

  openForEdit() {
    this.modalHeader = `Reassign ${this.onboardingStep} task`;
    let toEdit = new Account();
    toEdit.Email = this.currentAssignee;
    this.emailPreCheck = true;
    super.openForEdit(toEdit);
  }

  openForNew() {
    this.modalHeader = `Assign ${this.onboardingStep} task`;
    super.openForNew();
  }

  generateUpdateObject(resetPassword: boolean) : any {
    let request: any = {};

    request.Email = this.data.Email;
    request.Name = this.data.Name;
    request.AssignerName = JSON.parse(localStorage["session"]).Name;
    request.OnboardingStep = this.onboardingStep;
    request.ResetPassword = resetPassword;

    return request;
  }
}

